import styled from '@emotion/styled'
import { useBoolean, useMount, useRequest } from 'ahooks'
import { message } from 'antd'
import classNames from 'classnames'
import { find, isNil, isUndefined, omit } from 'lodash-es'
import { rgba } from 'polished'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import copy from 'copy-to-clipboard'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { FlowStatus, getFlowExecuteLog } from '@/apis/flow'
import type { logType } from '@/apis/run'
import {
  Button,
  Checkbox,
  IconFont,
  NodeForm,
  RunningLoading,
  Segmented,
} from '@/components'
import theme from '@/constants/theme.ts'
import { CodeEditor } from '@/features/editor'
import { FlowLogger } from '@/features/logger'
import type { FieldItem, StartNodeTypes } from '@/features/nodes/start'
import { Webhook } from '@/features/nodes/start/components/Webhook'
import { useNodeLocateByName } from '../hooks/useNodeLocateByName'
import type { FlowOutput } from '@/features/logger/types'

import { parseCardData } from '@/features/logger/utils/parse'
import { getCorrectResult, updateBatchTestData } from '@/apis/batchTest'
import { processObject } from '@/pages/application/components/appDetail/utils'
import { LimitedAlertContent } from '@/features/pay/LimitedAlert'
import { LIMITED_ERROR_COMBO_NOT_ENOUGH } from '@/constants/pay'
import { addChildrenToFlowOutput } from '@/features/logger/utils/logItem'
import { transformDataToValidLogs } from '@/features/logger/utils'
import {
  DEBUG_FORM_VALUE_MAP,
  DEBUG_JSON_VALUE_MAP,
  DEBUG_TEST_ID_MAP,
  FLOW_DEBUG_MAP,
} from '@/features/logger/constants'
import { NodeType } from '@/features/nodes/base'
import { transformConfig2SchemaConfig } from '@/features/nodes/start/utils.ts'
import Detail from './detail'
import { Output } from './Output'
import { TestDataSelect } from './TestDataSelect'
import { Message } from './Message'
import { useSessionValue } from './hooks/useSessionValue'

const statusInfo = {
  [FlowStatus.SUCCEEDED]: {
    text: '运行成功',
    color: '#2cb969',
  },
  [FlowStatus.FAIL]: {
    text: '运行失败',
    color: theme.colors.error,
  },
}

export interface RunDebugModalProps {
  debugTaskId?: string
  flowId: string
  defaultTab: string
  status?: FlowStatus
  top?: number
  detail?: logType['data'][0]
  disabled?: boolean
  mode?: 'debug' | 'record'
  hiddenButton?: boolean
  initFormValue?: Record<string, any>
  loading: boolean
  stream?: boolean
  inputInfo: {
    type?: StartNodeTypes | null
    config: FieldItem[]
  }
  output?: {
    result?: string | null
    errorMsg?: string | null
    stepName?: string | null
  }
  logs: FlowOutput[]
  showInput?: boolean
  onUpdateTestData: () => void
  onClose: () => void
  onInputsChange: (values: any) => void
  onExecute: (values: any) => void
  onRetry?: () => void
}

const ModalWrapper = styled.div<{ top: number; width?: number }>`
  position: absolute;
  top: ${({ top }) => top}px;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width || '600'}px;
  border-radius: 8px;
  background-color: #fff;
  backdrop-filter: blur(16px);
  z-index: 3;
  box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.2);
  z-index: 6;
  & {
    .run-btn {
      &:disabled {
        color: #f7f7ff;
        background: linear-gradient(
            0deg,
            rgba(255, 255, 255, 0.8),
            rgba(255, 255, 255, 0.8)
          ),
          #7b61ff;
      }
    }
  }
`

const StyledSegmented = styled(Segmented)`
  margin-bottom: 12px;
  font-size: 14px;
  .ant-segmented-item {
    width: 184px;
    padding: 8px 16px;
  }
`

export default function RunDebugModal(props: RunDebugModalProps) {
  const {
    debugTaskId,
    flowId,
    mode = 'debug',
    detail,
    defaultTab,
    status,
    loading = false,
    stream = false,
    inputInfo,
    initFormValue,
    output,
    logs: _logs,
    showInput = true,
    onUpdateTestData,
    onClose,
    onInputsChange,
    onExecute,
    onRetry,
  } = props

  const [form] = NodeForm.useForm()
  const { locateNode } = useNodeLocateByName()

  const [flowDebugInfo, setFlowDebugInfo] = useSessionValue(
    FLOW_DEBUG_MAP,
    flowId,
  )

  const [testDataId, setTestDataId] = useSessionValue(DEBUG_TEST_ID_MAP, flowId)
  const [formValue, setFormValue] = useSessionValue(
    DEBUG_FORM_VALUE_MAP,
    flowId,
  )
  const [jsonValue, setJsonValue] = useSessionValue(
    DEBUG_JSON_VALUE_MAP,
    flowId,
  )

  const [activeKey, setActiveKey] = useState(defaultTab || 'input')
  const [expandLogKeys, setExpandLogKeys] = useState<Array<string | number>>([])
  const [logs, setLogs] = useState(_logs)
  const [testOptionId, setTestOptionId] = useState('')
  const [testResultId, setTestResultId] = useState('')
  const [correctResult, setCorrectResult] = useState('')
  const [remarks, setRemarks] = useState('')
  const [saveToTest, setSaveToTest] = useState(false)
  const [diff, { toggle: toggleDiff, setFalse: closeDiff }] = useBoolean(false)
  const [showMessage, { setFalse: closeMessage, setTrue: openMessage }] =
    useBoolean(false)

  const updateValue = (newValue: Record<string, any>) => {
    if (!newValue) return
    if (inputInfo.type === 'form') {
      setFormValue(newValue)
      form.resetFields()
      form.setFieldsValue(newValue)
    }

    if (inputInfo.type === 'webhook') {
      let jsonText = ''
      try {
        jsonText = JSON.stringify(newValue)
      } catch {}
      setJsonValue(jsonText)
    }

    onInputsChange(newValue)
  }

  useRequest(() => getFlowExecuteLog({ flowId, taskId: debugTaskId! }), {
    ready: !!inputInfo.type && !!flowId && !!debugTaskId,
    refreshDeps: [debugTaskId],
    onSuccess(data) {
      const startInput = find(
        data?.run_result?.actionOutputs,
        each => each.type === NodeType.START,
      )
      const inputs = processObject(startInput?.input, ['run_args'])
      updateValue(inputs)
      setTestDataId(data.test_data_id ?? '')
      setTestResultId(data.test_result_id ?? '')
    },
  })

  useRequest(() => getCorrectResult(testResultId), {
    ready: !!testResultId,
    refreshDeps: [testResultId],
    onSuccess(data) {
      setRemarks(data?.remarks)
      setCorrectResult(data?.correct_result)
    },
  })

  const { runAsync: addNewTest } = useRequest(
    data => updateBatchTestData(flowId, data),
    {
      manual: true,
    },
  )

  const saveDataToTest = async () => {
    let data = formValue

    if (inputInfo.type === 'webhook') {
      data = {
        start: jsonValue,
      }
    }
    const res = await addNewTest(data)
    onUpdateTestData?.()
    if (!res) return
    setTestDataId(res.test_data_id)
    setTestOptionId(res.test_data_id)
    return res
  }

  const result = useMemo(() => output?.result, [output?.result])
  const hasResult = useMemo(
    () => !!output?.result || !!output?.errorMsg || status === 'SUCCEEDED',
    [output, status],
  )

  const showLoading = useMemo(() => {
    if (stream) {
      if (!!result || !!output?.errorMsg) return false
      return loading
    }
    return loading
  }, [loading, stream, output?.result])

  const tabs = useMemo(() => {
    const res = []
    if (mode === 'record') {
      res.push({
        label: '详情',
        value: 'detail',
      })
    }
    if (showInput) {
      res.push({
        label: '输入',
        value: 'input',
      })
    }
    res.push(
      {
        label: '输出',
        value: 'output',
        disabled: mode === 'debug' && !hasResult,
      },
      {
        label: '日志',
        value: 'logs',
        disabled: mode === 'debug' && !hasResult,
      },
    )
    return res
  }, [showInput, mode, hasResult])

  const knowledgeBucket = useMemo(() => {
    if (!logs || !output || !output.result) return false
    const outNode = logs[logs.length - 2]

    // 这里根据 mimetype 判断是否是不合理，但上下文环境中只能获取到这个
    const cardList = parseCardData(output.result)
    if (cardList.some(each => !each.mimetype)) return false
    if (!outNode || outNode.input?.memoryType !== 'searchMemory') return false
    return outNode.input.memory
  }, [logs])

  const onTabChange = (key: any) => {
    setActiveKey(key)
  }

  const clearFlowDebugMap = () => {
    setFlowDebugInfo(null)
  }

  const onValuesChange = (_changedValues: any, allValues: any) => {
    clearFlowDebugMap()
    setFormValue(allValues)
    setTestDataId('')
    setRemarks('')
    setCorrectResult('')
    onInputsChange(allValues)
  }

  const onJsonValueChange = (value: string) => {
    clearFlowDebugMap()
    setJsonValue(value)
    setTestDataId('')
    setRemarks('')
    setCorrectResult('')

    try {
      onInputsChange(JSON.parse(value))
    } catch (error) {}
  }

  const onCopy = (text: string) => {
    copy(text)
    message.success('复制成功')
  }

  const handleRunButtonClick = async () => {
    if (mode === 'record') {
      onRetry?.()
    } else {
      await form.validateFields()
      setActiveKey('output')
      let values = {}
      if (inputInfo.type === 'form') {
        values = form.getFieldsValue()
      } else {
        try {
          values = JSON.parse(jsonValue)
        } catch (error) {
          // err
        }
      }
      onExecute(values)
      if (!testDataId && saveToTest) {
        await saveDataToTest()
      }
    }
  }

  const handleTestDataChange = (value: string, data?: Record<string, any>) => {
    clearFlowDebugMap()
    setTestDataId(value)

    setRemarks(data?.remarks ?? '')
    setCorrectResult(data?.correct_result ?? '')

    if (data) {
      const inputs = omit(data, 'correct_result', 'remarks')
      if (inputInfo.type === 'webhook') {
        updateValue(inputs.start)
      } else {
        updateValue(inputs)
      }
    }
  }

  const onClear = () => {
    clearFlowDebugMap()
    setTestDataId('')
    setRemarks('')
    setCorrectResult('')
    setFormValue({})
    form.resetFields()
    setJsonValue('')
  }

  const onSaveToTest = async (event: CheckboxChangeEvent) => {
    setSaveToTest(event.target.checked)
  }

  useEffect(() => {
    if (initFormValue) {
      updateValue(initFormValue)
    }
  }, [initFormValue])

  useEffect(() => {
    setLogs(_logs)
  }, [_logs])

  useEffect(() => {
    setActiveKey(defaultTab)
  }, [defaultTab])

  useEffect(() => {
    if (!correctResult) {
      closeDiff()
    }
  }, [correctResult])

  useEffect(() => {
    const errLog = _logs.find(log => log.status === FlowStatus.FAIL)

    if (errLog && errLog?.type !== 'SUB_FLOW') {
      setExpandLogKeys([errLog.name])
    } else {
      setExpandLogKeys(['output'])
    }
  }, [_logs])

  const onChange = (item: FlowOutput, open: boolean) => {
    const key = item.value || ''
    console.log('subFlowLogs', item, key, open)

    if (item.type === 'SUB_FLOW' && open) {
      const flowId = item?.attrs?.flow_id
      const taskId = item?.attrs?.task_id

      getFlowExecuteLog({ flowId, taskId }).then(res => {
        const changedLogs = addChildrenToFlowOutput(
          logs,
          key,
          transformDataToValidLogs(res.run_result.actionOutputs, {
            isSingleStepRun: (res.run_type as string) === 'STEP_RUN',
            taskId: res.task_id,
          }),
        )

        setLogs(changedLogs)
      })
    }
  }

  useMount(() => {
    if (!flowDebugInfo || !flowId) return

    if (flowDebugInfo.from === 'batchTest' && !isNil(flowDebugInfo.index)) {
      openMessage()
    }
  })

  useMount(() => {
    let data = formValue
    if (inputInfo.type === 'webhook') {
      try {
        data = JSON.parse(String(jsonValue) ?? '')
      } catch {}
    }
    updateValue(data!)
  })

  return (
    <ModalWrapper top={props.top || 68} width={diff ? 1000 : 600}>
      {mode === 'debug' && !isNil(flowDebugInfo?.index) && (
        <Message
          className='absolute top-36px left-0 right-0 mx-auto'
          show={showMessage}
          onClose={closeMessage}
          icon={<IconFont name='chenggongbaocun' className='text-16px' />}
          content={`已选择测试集第${flowDebugInfo?.index + 1}条`}
        />
      )}
      <div className='flex flex-items-center flex-justify-between h-56px shrink-0 flex-grow-0 p-l-20px p-r-16px border-b-1 border-b-#e1e1e5 border-b-op-60 relative'>
        <div className='flex flex-items-center'>
          <span className='m-r-12 text-16px font-600 c-#17171d'>
            {detail
              ? `日志 ${dayjs(detail.start_time).format('YYYY-MM-DD HH:mm:ss')}`
              : '运行调试'}
          </span>
          {status && (
            <span
              className='h-20px line-height-20px p-l-8 p-r-8 text-12px font-500 b-rd-4px'
              style={{
                color: `${statusInfo[status].color}`,
                backgroundColor: rgba(statusInfo[status].color, 0.12),
              }}
            >
              {statusInfo[status].text}
            </span>
          )}
        </div>
        <div
          className='w-24px h-24px b-rd-4px flex flex-items-center flex-justify-center cursor-pointer hover:bg-#626999 hover:bg-op-12'
          onClick={onClose}
        >
          <IconFont name='guanbi' className='text-16px' />
        </div>
      </div>
      <div
        className={classNames('flex-1 flex overflow-hidden flex-col p-16px ', {
          'p-b-72': !props.hiddenButton,
        })}
      >
        <StyledSegmented
          disabled={loading}
          block
          value={activeKey}
          options={tabs}
          onChange={onTabChange}
        />
        <div className='flex flex-col overflow-hidden flex-1 shrink-0'>
          <div
            className={classNames(
              'flex-1 shrink-0 flex flex-col overflow-auto',
              {
                hidden: activeKey !== 'input',
              },
            )}
          >
            {mode === 'debug' && !!inputInfo.config.length && (
              <TestDataSelect
                className='mb-24px'
                optionId={testOptionId}
                flowId={flowId}
                value={testDataId}
                onChange={handleTestDataChange}
                popWidth={diff ? 944 : 544}
              />
            )}

            {inputInfo.type === 'form' && (
              <NodeForm
                className={inputInfo.config.length ? 'h-auto!' : ''}
                form={form}
                disabled={props.disabled}
                schema={transformConfig2SchemaConfig(inputInfo.config)}
                onValuesChange={onValuesChange}
              />
            )}
            {inputInfo.type === 'webhook' && (
              <>
                <Webhook style={{ padding: 0, marginBottom: 20 }} />
                <div className='flex-1'>
                  <CodeEditor
                    width='100%'
                    theme='tomorrow'
                    mode='json'
                    disabled={props.disabled}
                    setOptions={{
                      maxLines: 31,
                      hasCssTransforms: false,
                    }}
                    placeholder='填写JSON参数，模拟Webhook调用'
                    value={
                      isUndefined(jsonValue) ? '' : String(jsonValue) ?? ''
                    }
                    onChange={onJsonValueChange}
                    copy
                    onCopy={onCopy}
                  />
                </div>
              </>
            )}
          </div>
          {activeKey === 'output' && (
            <div className='flex-1 shrink-0 overflow-hidden mb-12px'>
              {showLoading && (
                <div className='flex-center flex-col h-100%'>
                  <RunningLoading loading={loading} />
                </div>
              )}
              {!showLoading && status === FlowStatus.FAIL && (
                <div className='min-h-66px flex flex-items-center b-rd-8px b-1 b-#ff5219 b-op-12 bg-#fff1ed px-16px py-8px'>
                  <IconFont name='cuowu' className='text-16px m-r-8' />
                  <div className='c-#e54a16 text-wrap line-height-20px'>
                    {output?.errorMsg ===
                    // 根据 errorMessage 判断是否要显示超期/耗尽的错误信息
                    LIMITED_ERROR_COMBO_NOT_ENOUGH ? (
                      <LimitedAlertContent prefix='该工作流所在的空间' />
                    ) : (
                      output?.errorMsg
                    )}
                  </div>
                  {output?.stepName && (
                    <Button
                      className='p-0! ml-8px font-normal!'
                      type='link'
                      onClick={() => {
                        if (output.stepName) {
                          setExpandLogKeys([output.stepName])
                          locateNode(output.stepName)
                        }
                        setActiveKey('logs')
                      }}
                    >
                      查看日志
                    </Button>
                  )}
                </div>
              )}
              {!showLoading && status !== FlowStatus.FAIL && (
                <div className='h-full flex border-1px rounded-8px'>
                  {isNil(output?.result) && (
                    <RunningLoading loading={loading} />
                  )}
                  {diff && (
                    <Output
                      title='正确答案'
                      className='w-48% flex-1 h-full border-r-1px py-20px'
                      diff={true}
                      result={correctResult}
                      remarks={remarks}
                      operations={['copy']}
                    />
                  )}
                  {!isNil(output?.result) && (
                    <Output
                      knowledgeBucket={!diff && knowledgeBucket}
                      scrollToEnd={stream}
                      title='运行结果'
                      className={classNames(
                        'h-full py-20px',
                        diff ? 'w-48% flex-1' : 'w-100%',
                      )}
                      diff={diff}
                      result={output?.result}
                      onDiff={toggleDiff}
                      operations={
                        loading
                          ? []
                          : correctResult
                          ? ['diff', 'copy']
                          : ['copy']
                      }
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {activeKey === 'logs' && (
            <div className='flex-1 shrink-0 overflow-auto mb-12px'>
              <FlowLogger
                outputs={logs}
                activeKey={expandLogKeys}
                onChange={onChange}
              />
            </div>
          )}
          {activeKey === 'detail' && !!props.detail && (
            <Detail detail={props.detail} />
          )}
        </div>

        {!testDataId &&
          mode === 'debug' &&
          activeKey === 'input' &&
          !!inputInfo.config.length && (
            <div className='flex mt-8px'>
              <Checkbox
                className='mb-8px!'
                checked={saveToTest}
                onChange={onSaveToTest}
              >
                运行结束后，把输入加入测试集
              </Checkbox>
            </div>
          )}

        {!props.hiddenButton && (
          <div className='absolute w-100% flex gap-12px left-0 bottom-0 h-72px p-16px b-t-1 b-t-#e1e1e5 b-t-op-60'>
            <Button
              className='w-36px! flex-none'
              icon={<IconFont name='qingkongrizhi' />}
              onClick={onClear}
            />
            <Button
              type='primary'
              loading={loading}
              className='run-btn flex-auto c-#f7f7ff'
              onClick={handleRunButtonClick}
            >
              {mode === 'record'
                ? '重新运行'
                : loading
                ? '运行中'
                : activeKey === 'input'
                ? '开始运行'
                : '重新运行'}
            </Button>
          </div>
        )}
      </div>
    </ModalWrapper>
  )
}
